"use client";

import { useEffect, useState } from "react";
import { Button, buttonVariants } from "@/components/ui/button";
import Script from 'next/script';
import { cn } from "@/lib/utils";
import * as Popover from "@radix-ui/react-popover";
import { Skeleton } from "@/components/ui/skeleton";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import { useTranslations } from "next-intl";
import { useLocale } from "next-intl";

// Helper function to format currency
const formatCurrency = (amount: number, locale: string) => {
  if (locale === 'id') {
    // Format IDR without decimals
    return new Intl.NumberFormat('id-ID').format(Math.round(amount));
  } else {
    // Format USD with 2 decimal places
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }
};

// Helper function to convert IDR to USD
const convertIDRtoUSD = (idrAmount: number, conversionRate: number) => {
  // Return exact conversion without rounding
  return idrAmount * conversionRate;
};


const SectionPricingTables = () => {
  const t = useTranslations("PricingTable");
  const locale = useLocale()
  const [isAnnual, setIsAnnual] = useState(false);
  const [conversionRate, setConversionRate] = useState<number>(0);

  const isRTL = locale === 'ar';


  // Fetch conversion rate
  useEffect(() => {
    const fetchConversionRate = async () => {
      try {
        const response = await fetch('https://latest.currency-api.pages.dev/v1/currencies/idr.json');
        const data = await response.json();
        setConversionRate(data.idr.usd);
      } catch (error) {
        console.error('Error fetching conversion rate:', error);
        // Fallback conversion rate if API fails
        setConversionRate(0.000065);
      }
    };

    fetchConversionRate();
  }, []);

  // Define base prices in IDR
  const basePrices = {
    basic: {
      monthly: 1490000,
      annual: 17880000,
      discountedAnnual: 16986000
    },
    business: {
      monthly: 3499000,
      annual: 41988000,
      discountedAnnual: 39888600
    },
  };


  const getPricingTables = (t: any) => [
    {
      title: t("Basic.title"),
      currency: locale === 'id' ? "IDR" : "USD",
      monthlyPrice: locale === 'id'
        ? formatCurrency(basePrices.basic.monthly, locale)
        : formatCurrency(convertIDRtoUSD(basePrices.basic.monthly, conversionRate), locale),
      annualPrice: locale === 'id'
        ? formatCurrency(basePrices.basic.annual, locale)
        : formatCurrency(convertIDRtoUSD(basePrices.basic.annual, conversionRate), locale),
      discountedAnnualPrice: locale === 'id'
        ? formatCurrency(basePrices.basic.discountedAnnual, locale)
        : formatCurrency(convertIDRtoUSD(basePrices.basic.discountedAnnual, conversionRate), locale),
      term: t("Basic.term"),
      description: t("Basic.description"),
      features: [
        { text: t("Basic.features.logo"), available: true, tooltip: t("Basic.tooltips.logo") },
        { text: t("Basic.features.brandingKit"), available: false, tooltip: t("Basic.tooltips.brandingKit") },
        { text: t("Basic.features.website"), available: true, tooltip: t("Basic.tooltips.website") },
        { text: t("Basic.features.ecommerce"), available: false, tooltip: t("Basic.tooltips.ecommerce") },
        { text: t("Basic.features.mobileApp"), available: false, tooltip: t("Basic.tooltips.mobileApp") },
        { text: t("Basic.features.hosting"), available: true, tooltip: t("Basic.tooltips.hosting") },
        { text: t("Basic.features.seo"), available: true, tooltip: t("Basic.tooltips.seo") },
        { text: t("Basic.features.socialMedia"), available: false, tooltip: t("Basic.tooltips.socialMedia") },
        { text: t("Basic.features.freeDomain"), available: true, tooltip: t("Basic.tooltips.freeDomain") },
        { text: t("Basic.features.whatsapp"), available: false, tooltip: t("Basic.tooltips.whatsapp") },
        { text: t("Basic.features.aiChatbot"), available: false, tooltip: t("Basic.tooltips.aiChatbot") },
      ],
      ctaText: t("Basic.ctaText"),
      ctaLink: "https://mudin.myr.id/pl/mdn-digital-agency-basic?iframe=true",
      isMayar: true,
      featured: false,
    },
    {
      title: t("Bisnis.title"),
      currency: locale === 'id' ? "IDR" : "USD",
      monthlyPrice: locale === 'id'
        ? formatCurrency(basePrices.business.monthly, locale)
        : formatCurrency(convertIDRtoUSD(basePrices.business.monthly, conversionRate), locale),
      annualPrice: locale === 'id'
        ? formatCurrency(basePrices.business.annual, locale)
        : formatCurrency(convertIDRtoUSD(basePrices.business.annual, conversionRate), locale),
      discountedAnnualPrice: locale === 'id'
        ? formatCurrency(basePrices.business.discountedAnnual, locale)
        : formatCurrency(convertIDRtoUSD(basePrices.business.discountedAnnual, conversionRate), locale),
      term: t("Bisnis.term"),
      description: t("Bisnis.description"),
      features: [
        { text: t("Bisnis.features.logo"), available: true, tooltip: t("Bisnis.tooltips.logo") },
        { text: t("Bisnis.features.brandingKit"), available: true, tooltip: t("Bisnis.tooltips.brandingKit") },
        { text: t("Bisnis.features.website"), available: true, tooltip: t("Bisnis.tooltips.website") },
        { text: t("Bisnis.features.ecommerce"), available: true, tooltip: t("Bisnis.tooltips.ecommerce") },
        { text: t("Basic.features.mobileApp"), available: true, tooltip: t("Bisnis.tooltips.mobileApp") },
        { text: t("Bisnis.features.hosting"), available: true, tooltip: t("Bisnis.tooltips.hosting") },
        { text: t("Bisnis.features.seo"), available: true, tooltip: t("Bisnis.tooltips.seo") },
        { text: t("Bisnis.features.socialMedia"), available: false, tooltip: t("Bisnis.tooltips.socialMedia") },
        { text: t("Bisnis.features.freeDomain"), available: true, tooltip: t("Bisnis.tooltips.freeDomain") },
        { text: t("Bisnis.features.whatsapp"), available: false, tooltip: t("Bisnis.tooltips.whatsapp") },
        { text: t("Bisnis.features.aiChatbot"), available: true, tooltip: t("Bisnis.tooltips.aiChatbot") },
      ],
      ctaText: t("Bisnis.ctaText"),
      ctaLink: "https://mudin.myr.id/pl/mdn-digital-agency-business?iframe=true",
      isMayar: true,
      featured: true,
    },
    {
      title: t("Custom.title"),
      monthlyPrice: t("Custom.ctaText"),
      annualPrice: t("Custom.ctaText"),
      discountedAnnualPrice: t("Custom.ctaText"),
      // term: t("Custom.term"),
      description: t("Custom.description"),
      features: [
        { text: t("Custom.features.logo"), available: true, tooltip: t("Custom.tooltips.logo") },
        { text: t("Custom.features.brandingKit"), available: true, tooltip: t("Custom.tooltips.brandingKit") },
        { text: t("Custom.features.website"), available: true, tooltip: t("Custom.tooltips.website") },
        { text: t("Custom.features.ecommerce"), available: true, tooltip: t("Custom.tooltips.ecommerce") },
        { text: t("Custom.features.mobileApp"), available: true, tooltip: t("Custom.tooltips.mobileApp") },
        { text: t("Custom.features.hosting"), available: true, tooltip: t("Custom.tooltips.hosting") },
        { text: t("Custom.features.seo"), available: true, tooltip: t("Custom.tooltips.seo") },
        { text: t("Custom.features.socialMedia"), available: true, tooltip: t("Custom.tooltips.socialMedia") },
        { text: t("Custom.features.freeDomain"), available: true, tooltip: t("Custom.tooltips.freeDomain") },
        { text: t("Custom.features.whatsapp"), available: true, tooltip: t("Custom.tooltips.whatsapp") },
        { text: t("Custom.features.aiChatbot"), available: true, tooltip: t("Custom.tooltips.aiChatbot") },
      ],
      ctaText: t("Custom.ctaText"),
      ctaLink: "#quotation-section",
      isMayar: false,
      featured: false,
    },
    // Repeat similar structure for Business and Custom plans...
  ];

  const handleToggle = () => setIsAnnual(!isAnnual);

  const handleClick = (ctaLink: string | undefined) => {
    if (ctaLink) {
      window.location.href = ctaLink;
    } else {
      window.location.href = "#quotation-section";
    }
  };

  const pricingTables = getPricingTables(t);
  return (
    <section className="py-16 lg:py-24">
      <div className="container">
        {/* Toggle Annual/Monthly Plan */}
        <div className={cn(
          "flex items-center justify-center mb-8",
          isRTL ? "flex-row-reverse" : "flex-row"
        )}>
          <span className={cn(
            "text-sm md:text-base lg:text-lg",
            isRTL ? "ml-4" : "mr-4"
          )}>
            {t("Monthly")}
          </span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only"
              checked={isAnnual}
              onChange={handleToggle}
            />
            <div className="w-14 h-8 bg-gray-200 rounded-full shadow-inner"></div>
            <div
              className={cn(
                "absolute top-1 w-6 h-6 bg-blue-500 rounded-full shadow transform transition-transform",
                isRTL
                  ? isAnnual ? "right-1" : "right-7"
                  : isAnnual ? "left-7" : "left-1"
              )}
            ></div>
          </label>
          <span className={cn(
            "text-sm md:text-base lg:text-lg",
            isRTL ? "mr-4" : "ml-4"
          )}>
            {t("Annual")}
          </span>
          <span className={cn(
            "text-sm md:text-base lg:text-lg font-semibold text-green-600 bg-green-100 px-2 py-1 rounded-full shadow-md",
            isRTL ? "mr-4" : "ml-4"
          )}>
            {t("Discount")}
          </span>
        </div>

        {/* Pricing Tables */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-10">
          {pricingTables.map(
            (
              {
                title,
                currency,
                monthlyPrice,
                annualPrice,
                discountedAnnualPrice,
                description,
                features,
                ctaText,
                ctaLink,
                featured,
                isMayar,
              },
              index,
            ) => {
              const BASIC_TITLE = t("Basic.title");
              const BUSINESS_TITLE = t("Bisnis.title");
              const isDiscounted = isAnnual && (title === BASIC_TITLE || title === BUSINESS_TITLE);
              const price = isAnnual
                ? isDiscounted
                  ? discountedAnnualPrice
                  : annualPrice
                : monthlyPrice;
              const originalPrice = isDiscounted ? annualPrice : null;

              // Tampilkan skeleton jika harga belum tersedia
              const isPriceLoading = !conversionRate || price === "0";

              return (
                <div
                  key={index}
                  className={`${featured ? "shadow-lg before:w-full" : "before:w-0"
                    } hover-shadow z-[1] overflow-hidden rounded-md bg-white px-10 py-12 before:absolute before:left-0 before:top-0 before:block before:h-[.3125rem] before:bg-secondary before:transition-all before:duration-200 hover:before:w-full dark:bg-slate-800`}
                >
                  {title && <h3 className="mb-2 text-md">{title}</h3>}
                  <div className="mb-4 text-foreground dark:text-white">
                    {currency && (
                      <span className="block text-2xl">{currency}</span>
                    )}
                    {originalPrice && !isPriceLoading && (
                      <div className="text-gray-400 text-sm md:text-base lg:text-md line-through">
                        {originalPrice}
                      </div>
                    )}
                    <span className="block text-4xl font-bold tracking-tighter md:text-6xl lg:text-7xl">
                      {isPriceLoading ? (
                        <Skeleton className="h-10 w-20" /> // Skeleton loader
                      ) : (
                        price
                      )}
                    </span>
                    <span className="ml-1.5 inline-block text-xl text-slate-400">
                      {!isPriceLoading && (isAnnual ? t("PerYear") : t("PerMonth"))}
                    </span>
                  </div>
                  {description && <p className="mb-4 font-bold">{description}</p>}
                  <ul className="mb-8">
                    {features.map((feature, idx) => (
                      <li key={idx} className="flex items-center">
                        <span className="mr-2">{feature.available ? "✅" : "❌"}</span>
                        <span>{feature.text}</span>
                        <Popover.Root>
                          <Popover.Trigger asChild>
                            <span className="ml-2 cursor-pointer">
                              <QuestionMarkCircleIcon className="h-4 w-4 text-gray-500 inline-block" aria-hidden="true" />
                            </span>
                          </Popover.Trigger>
                          <Popover.Portal>
                            <Popover.Content className="popover-content bg-black text-white border border-gray-300 p-2 rounded-md shadow-lg text-sm z-50">
                              {feature.tooltip}
                              <Popover.Arrow className="fill-white" />
                            </Popover.Content>
                          </Popover.Portal>
                        </Popover.Root>
                      </li>
                    ))}
                  </ul>

                  {ctaText && (
                    isMayar ? (
                      <a
                        className={cn(
                          "iframe-lightbox-link",
                          buttonVariants({ variant: "default", size: "lg" })
                        )}
                        href={ctaLink}
                        data-padding-bottom="30%"
                        data-scrolling="true"
                      >
                        {ctaText}
                      </a>
                    ) : (
                      <Button
                        className="w-40"
                        size="lg"
                        onClick={() => handleClick(ctaLink)}
                      >
                        {ctaText}
                      </Button>
                    )
                  )}
                </div>
              );
            }
          )}
        </div>
        <p className="mt-10 text-center text-sm text-gray-600">
          {t("FooterNote")}
          <br />
          {t("FooterNote2")}
        </p>
      </div>
      <Script
        src="https://mayarembed.r2.mayar.id/mayar-new-min.js"
        strategy="lazyOnload"
        onLoad={() => {
          // console.log("Mayar sudah siap!");
        }}
      />
    </section>
  );
};

export default SectionPricingTables;

